import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { Warning } from '../../components/LessonUI';

const HTMLGeolocation = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">HTML5 APIs</div>
                <h1>Geolocation API</h1>
                <p className="hero-lead">
                    Access user location with the HTML5 Geolocation API. Build location-aware applications
                    like store finders, weather apps, and local search features.
                </p>
            </div>

            <div className="content-section">
                <h2>What is the Geolocation API?</h2>
                <p>
                    The Geolocation API allows websites to request the user's geographic location.
                    It provides latitude, longitude, altitude, speed, and accuracy information.
                </p>

                <div className="features-grid">
                    <div className="feature-card">
                        <h3> Coordinates</h3>
                        <p>Get latitude and longitude</p>
                    </div>
                    <div className="feature-card">
                        <h3> Accuracy</h3>
                        <p>Know precision of location data</p>
                    </div>
                    <div className="feature-card">
                        <h3> Privacy-First</h3>
                        <p>Requires user permission</p>
                    </div>
                    <div className="feature-card">
                        <h3> Mobile & Desktop</h3>
                        <p>Works on all modern devices</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Basic Geolocation Request</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <title>Geolocation Demo</title>
</head>
<body>
    <button onclick="getLocation()">Get My Location</button>
    <div id="result"></div>

    <script>
    function getLocation() {
        if (navigator.geolocation) {
            // Browser supports geolocation
            navigator.geolocation.getCurrentPosition(
                showPosition,  // Success callback
                showError     // Error callback
            );
        } else {
            document.getElementById('result').textContent = 
                'Geolocation is not supported by this browser.';
        }
    }

    function showPosition(position) {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        const accuracy = position.coords.accuracy;
        
        document.getElementById('result').innerHTML = \`
            <p><strong>Latitude:</strong> \${lat}</p>
            <p><strong>Longitude:</strong> \${lon}</p>
            <p><strong>Accuracy:</strong> \${accuracy} meters</p>
        \`;
    }

    function showError(error) {
        const messages = {
            1: 'Permission denied',
            2: 'Position unavailable',
            3: 'Request timeout'
        };
        document.getElementById('result').textContent = 
            'Error: ' + messages[error.code];
    }
    </script>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Position Object Properties</h2>

                <h3>Coordinates (position.coords)</h3>
                <table className="reference-table">
                    <thead>
                        <tr>
                            <th>Property</th>
                            <th>Description</th>
                            <th>Always Available</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>latitude</code></td>
                            <td>Latitude in decimal degrees</td>
                            <td>Yes</td>
                        </tr>
                        <tr>
                            <td><code>longitude</code></td>
                            <td>Longitude in decimal degrees</td>
                            <td>Yes</td>
                        </tr>
                        <tr>
                            <td><code>accuracy</code></td>
                            <td>Accuracy in meters</td>
                            <td>Yes</td>
                        </tr>
                        <tr>
                            <td><code>altitude</code></td>
                            <td>Altitude in meters (above sea level)</td>
                            <td> Optional</td>
                        </tr>
                        <tr>
                            <td><code>altitudeAccuracy</code></td>
                            <td>Altitude accuracy in meters</td>
                            <td> Optional</td>
                        </tr>
                        <tr>
                            <td><code>heading</code></td>
                            <td>Direction of travel (degrees from north)</td>
                            <td> Optional</td>
                        </tr>
                        <tr>
                            <td><code>speed</code></td>
                            <td>Speed in meters per second</td>
                            <td> Optional</td>
                        </tr>
                    </tbody>
                </table>

                <div className="code-example">
                    <CodeBlock language="javascript" code={`function showPosition(position) {
    const coords = position.coords;
    
    console.log('Latitude:', coords.latitude);
    console.log('Longitude:', coords.longitude);
    console.log('Accuracy:', coords.accuracy, 'meters');
    
    // Optional properties (may be null)
    if (coords.altitude !== null) {
        console.log('Altitude:', coords.altitude, 'meters');
    }
    if (coords.speed !== null) {
        console.log('Speed:', coords.speed, 'm/s');
    }
    if (coords.heading !== null) {
        console.log('Heading:', coords.heading, 'degrees');
    }
    
    // Timestamp
    console.log('Time:', new Date(position.timestamp));
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Geolocation Options</h2>

                <div className="code-example">
                    <CodeBlock language="javascript" code={`const options = {
    enableHighAccuracy: true,  // Request best accuracy
    timeout: 10000,            // Max wait time (ms)
    maximumAge: 0              // Don't use cached position
};

navigator.geolocation.getCurrentPosition(
    showPosition,
    showError,
    options
);`} />
                </div>

                <h3>Options Explained</h3>
                <ul>
                    <li>
                        <strong>enableHighAccuracy:</strong> <code>true</code> uses GPS (more accurate but slower, more battery),
                        <code>false</code> uses WiFi/IP (faster but less accurate)
                    </li>
                    <li>
                        <strong>timeout:</strong> Maximum time (milliseconds) to wait for position.
                        Default is Infinity.
                    </li>
                    <li>
                        <strong>maximumAge:</strong> Maximum age (milliseconds) of cached position.
                        0 means always get fresh position. Default is 0.
                    </li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Watching Position (Live Updates)</h2>

                <div className="code-example">
                    <CodeBlock language="javascript" code={`let watchId;

// Start watching position
function startTracking() {
    watchId = navigator.geolocation.watchPosition(
        updatePosition,
        showError,
        { enableHighAccuracy: true }
    );
}

// Stop watching
function stopTracking() {
    if (watchId) {
        navigator.geolocation.clearWatch(watchId);
        console.log('Stopped tracking');
    }
}

// Called every time position changes
function updatePosition(position) {
    const { latitude, longitude, speed } = position.coords;
    
    console.log('Position update:');
    console.log('  Lat:', latitude);
    console.log('  Lon:', longitude);
    console.log('  Speed:', speed ? \`\${speed} m/s\` : 'Unknown');
    
    // Update map, etc.
    updateMap(latitude, longitude);
}

// Usage
document.getElementById('start-btn').onclick = startTracking;
document.getElementById('stop-btn').onclick = stopTracking;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Error Handling</h2>

                <div className="code-example">
                    <CodeBlock language="javascript" code={`function handleError(error) {
    switch(error.code) {
        case error.PERMISSION_DENIED:
            console.error('User denied geolocation request');
            showMessage('Please enable location access in browser settings');
            break;
            
        case error.POSITION_UNAVAILABLE:
            console.error('Location information unavailable');
            showMessage('Unable to determine your location');
            break;
            
        case error.TIMEOUT:
            console.error('Location request timed out');
            showMessage('Location request took too long');
            break;
            
        default:
            console.error('Unknown error:', error.message);
            showMessage('An error occurred while getting location');
    }
}

// Error codes
// error.PERMISSION_DENIED = 1
// error.POSITION_UNAVAILABLE = 2
// error.TIMEOUT = 3`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Practical Example: Store Finder</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <title>Store Finder</title>
    <style>
        .store { margin: 10px 0; padding: 10px; border: 1px solid #ddd; }
        .distance { color: #666; font-size: 0.9em; }
    </style>
</head>
<body>
    <h1>Find Nearest Store</h1>
    <button onclick="findNearestStores()">Find Stores Near Me</button>
    <div id="stores"></div>

    <script>
    const stores = [
        { name: 'Store A', lat: 40.7128, lon: -74.0060 },
        { name: 'Store B', lat: 34.0522, lon: -118.2437 },
        { name: 'Store C', lat: 41.8781, lon: -87.6298 }
    ];

    function findNearestStores() {
        navigator.geolocation.getCurrentPosition(position => {
            const userLat = position.coords.latitude;
            const userLon = position.coords.longitude;
            
            // Calculate distances
            const storesWithDistance = stores.map(store => ({
                ...store,
                distance: calculateDistance(
                    userLat, userLon,
                    store.lat, store.lon
                )
            }));
            
            // Sort by distance
            storesWithDistance.sort((a, b) => a.distance - b.distance);
            
            // Display
            const html = storesWithDistance.map(store => \`
                <div class="store">
                    <strong>\${store.name}</strong>
                    <div class="distance">\${store.distance.toFixed(1)} km away</div>
                </div>
            \`).join('');
            
            document.getElementById('stores').innerHTML = html;
        }, error => {
            alert('Unable to get location: ' + error.message);
        });
    }

    // Haversine formula for distance calculation
    function calculateDistance(lat1, lon1, lat2, lon2) {
        const R = 6371; // Earth radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * 
            Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
        
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    }
    </script>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Integration with Google Maps</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <title>Location on Map</title>
    <style>
        #map { height: 400px; width: 100%; }
    </style>
</head>
<body>
    <button onclick="showLocationOnMap()">Show My Location</button>
    <div id="map"></div>

    <script src="https://maps.googleapis.com/maps/api/js?key=YOUR_API_KEY"></script>
    <script>
    let map;

    function showLocationOnMap() {
        navigator.geolocation.getCurrentPosition(position => {
            const { latitude, longitude } = position.coords;
            
            // Create map centered on user location
            map = new google.maps.Map(document.getElementById('map'), {
                center: { lat: latitude, lng: longitude },
                zoom: 15
            });
            
            // Add marker at user location
            new google.maps.Marker({
                position: { lat: latitude, lng: longitude },
                map: map,
                title: 'You are here',
                icon: {
                    url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                }
            });
            
            // Add info window
            const infoWindow = new google.maps.InfoWindow({
                content: \`
                    <strong>Your Location</strong><br>
                    Lat: \${latitude.toFixed(4)}<br>
                    Lon: \${longitude.toFixed(4)}<br>
                    Accuracy: \${position.coords.accuracy.toFixed(0)}m
                \`
            });
            infoWindow.open(map, marker);
        }, error => {
            alert('Error: ' + error.message);
        });
    }
    </script>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Security & Privacy</h2>

                <h3>HTTPS Required</h3>
                <Warning><p> Geolocation API only works on HTTPS sites (except localhost).</p></Warning>

                <h3>User Permission</h3>
                <ul>
                    <li>Browser prompts user for permission</li>
                    <li>User can deny or allow</li>
                    <li>Permission is remembered for site</li>
                    <li>User can revoke permission anytime</li>
                </ul>

                <h3>Best Practices</h3>
                <ul>
                    <li> Explain why you need location before requesting</li>
                    <li> Only request when needed (not on page load)</li>
                    <li> Provide fallback if user denies permission</li>
                    <li> Don't store location longer than necessary</li>
                    <li> Be transparent about how location is used</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Browser Support</h2>
                <p>Geolocation API is supported in all modern browsers:</p>
                <ul>
                    <li>Chrome 5+</li>
                    <li>Firefox 3.5+</li>
                    <li>Safari 5+</li>
                    <li>Edge (all versions)</li>
                    <li>iOS Safari 3.2+</li>
                    <li>Android Browser 2.1+</li>
                </ul>

                <h3>Feature Detection</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`if ('geolocation' in navigator) {
    // Geolocation is supported
    navigator.geolocation.getCurrentPosition(/* ... */);
} else {
    // Fallback: ask user to enter location manually
    showManualLocationInput();
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Working Example</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <title>Geolocation Demo</title>
    <style>
        body { font-family: Arial, sans-serif; max-width: 600px; margin: 50px auto; }
        button { padding: 10px 20px; font-size: 16px; cursor: pointer; }
        #result { margin-top: 20px; padding: 20px; background: #f5f5f5; border-radius: 8px; }
        .loading { color: #666; }
        .error { color: #d32f2f; }
        .success { color: #388e3c; }
    </style>
</head>
<body>
    <h1>HTML5 Geolocation Demo</h1>
    <button onclick="getLocation()">Get My Location</button>
    <div id="result"></div>

    <script>
    function getLocation() {
        const result = document.getElementById('result');
        
        if (!navigator.geolocation) {
            result.innerHTML = '<p class="error">Geolocation not supported</p>';
            return;
        }
        
        result.innerHTML = '<p class="loading">Getting location...</p>';
        
        const options = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
        };
        
        navigator.geolocation.getCurrentPosition(
            position => {
                const { latitude, longitude, accuracy } = position.coords;
                
                result.innerHTML = \`
                    <p class="success"><strong>Location found!</strong></p>
                    <p><strong>Latitude:</strong> \${latitude.toFixed(6)}</p>
                    <p><strong>Longitude:</strong> \${longitude.toFixed(6)}</p>
                    <p><strong>Accuracy:</strong> \${accuracy.toFixed(0)} meters</p>
                    <p><a href="https://www.google.com/maps?q=\${latitude},\${longitude}" 
                          target="_blank">View on Google Maps</a></p>
                \`;
            },
            error => {
                const errors = {
                    1: 'Permission denied. Please enable location access.',
                    2: 'Position unavailable. Please check your device settings.',
                    3: 'Request timeout. Please try again.'
                };
                result.innerHTML = \`<p class="error">\${errors[error.code]}</p>\`;
            },
            options
        );
    }
    </script>
</body>
</html>`} />
                </div>
            </div>

        </div>
    );
};

export default HTMLGeolocation;


